import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import MatchingNodeView from './matching-node-view';
import MatchingPairDefinitionNodeView from './matching-pair-definition-node-view';
import MatchingPairExplanationNodeView from './matching-pair-explanation-node-view';
import MatchingPairNodeView from './matching-pair-node-view';
import MatchingPairTermNodeView from './matching-pair-term-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        matching: {
            insertMatching: (options?: InsertMatchingOptions) => ReturnType;
            insertBareMatching: (options?: InsertMatchingOptions) => ReturnType;
        };
    }
}

export interface InsertMatchingOptions {
    caption?: string;
    instruction?: string;
    pairs?: Array<{
        term: string;
        definition: string;
        explanation?: string;
    }>;
}

const defaultMatchingNode = (options: InsertMatchingOptions) => {
    const pairs = options.pairs || [
        {
            term: '',
            definition: '',
            explanation: '',
        },
        {
            term: '',
            definition: '',
            explanation: '',
        },
    ];

    // Create matching pair nodes
    const pairNodes = pairs.map(pair => ({
        type: 'matchingPair',
        content: [
            {
                type: 'matchingPairTerm',
                content: [
                    {
                        type: 'paragraph',
                        content: pair.term ? [{ type: 'text', text: pair.term }] : undefined,
                    },
                ],
            },
            {
                type: 'matchingPairDefinition',
                content: [
                    {
                        type: 'paragraph',
                        content: pair.definition ? [{ type: 'text', text: pair.definition }] : undefined,
                    },
                ],
            },
            {
                type: 'matchingPairExplanation',
                content: [
                    {
                        type: 'paragraph',
                        content: pair.explanation ? [{ type: 'text', text: pair.explanation }] : undefined,
                    },
                ],
            },
        ],
    }));

    const matchingNode = {
        type: 'matching',
        attrs: {
            caption: options.caption || 'Matching question',
        },
        content: pairNodes,
    };

    return matchingNode;
};

/**
 * Matching Node - The container node for a matching question activity.
 *
 * This is the top-level node that holds all matching pairs and metadata (id, caption).
 * Structure: matching > matchingPair[] (must have at least 2 pairs)
 */
export const MatchingNodeHeadless = Node.create({
    name: 'matching',

    group: 'block',

    content: 'matchingPair+',

    defining: true,

    isolating: true,

    selectable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-matching-id'),
                renderHTML: attributes => ({
                    'data-matching-id': attributes.id,
                }),
            },
            caption: {
                default: '',
                parseHTML: element => element.getAttribute('data-caption') || '',
                renderHTML: attributes => ({
                    'data-caption': attributes.caption,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="matching"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'matching',
                class: 'matching-container',
            }),
            0,
        ];
    },

    addCommands() {
        return {
            insertMatching:
                (options: InsertMatchingOptions = {}) =>
                ({ commands }) => {
                    const instructionNode = {
                        type: 'paragraph',
                        attrs: {
                            textAlign: null,
                        },
                        content: options.instruction ? [{ type: 'text', text: options.instruction }] : undefined,
                    };

                    const questionSet = {
                        type: 'questionSet',
                        attrs: {
                            activityType: 'participation',
                            questionType: 'matching',
                        },
                        content: [instructionNode, defaultMatchingNode(options)],
                    };

                    return commands.insertContent(questionSet);
                },
            insertBareMatching:
                (options: InsertMatchingOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent(defaultMatchingNode(options));
                },
        };
    },
});

/**
 * Matching Pair Node - Individual matching pair within a matching question.
 *
 * Each pair contains a term, definition, and explanation. Multiple pairs form a complete matching question.
 * Structure: matchingPair > matchingPairTerm + matchingPairDefinition + matchingPairExplanation
 */
export const MatchingPairNodeHeadless = Node.create({
    name: 'matchingPair',

    group: 'matchingContent',

    content: 'matchingPairTerm matchingPairDefinition matchingPairExplanation',

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-pair-id'),
                renderHTML: attributes => ({
                    'data-pair-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="matching-pair"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'matching-pair',
                class: 'matching-pair-item',
            }),
            0,
        ];
    },
});

// Matching Pair Term Node Extension
export const MatchingPairTermNodeHeadless = Node.create({
    name: 'matchingPairTerm',

    group: 'matchingPairContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-term-id'),
                renderHTML: attributes => ({
                    'data-term-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="matching-pair-term"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'matching-pair-term',
            }),
            0,
        ];
    },
});

// Matching Pair Definition Node Extension
export const MatchingPairDefinitionNodeHeadless = Node.create({
    name: 'matchingPairDefinition',

    group: 'matchingPairContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-definition-id'),
                renderHTML: attributes => ({
                    'data-definition-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="matching-pair-definition"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'matching-pair-definition',
            }),
            0,
        ];
    },
});

// Matching Pair Explanation Node Extension (separate from multiple choice explanation)
export const MatchingPairExplanationNodeHeadless = Node.create({
    name: 'matchingPairExplanation',

    group: 'matchingPairContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-matching-explanation-id'),
                renderHTML: attributes => ({
                    'data-matching-explanation-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="matching-pair-explanation"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'matching-pair-explanation',
            }),
            0,
        ];
    },
});

export const MatchingPairExplanationNode = MatchingPairExplanationNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MatchingPairExplanationNodeView);
    },
});

export const MatchingPairDefinitionNode = MatchingPairDefinitionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MatchingPairDefinitionNodeView);
    },
});

export const MatchingPairTermNode = MatchingPairTermNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MatchingPairTermNodeView);
    },
});

export const MatchingPairNode = MatchingPairNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MatchingPairNodeView);
    },
});

export const MatchingNode = MatchingNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MatchingNodeView);
    },
});
